import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { FC } from 'react'
import { DensityStyleTypes } from '../../../../types'

interface ContractedByOrg {
  org_uuid: string
  PK: string
  name: string
}

interface RenderContractedByProps {
  params: GridRenderCellParams
  densityStyles: DensityStyleTypes
  navigate: (route: string) => void
}

const RenderContractedByCell: FC<RenderContractedByProps> = ({ params, densityStyles, navigate }) => {
  const row = params?.row as any

  // Get resolved contracted by from the organization's contractedBy array
  const resolvedContractedBy: ContractedByOrg[] = row?._resolvedContractedBy || []

  // Fallback to legacy contractedBy if no resolved data
  const legacyContractedBy = row?.contractedBy

  // If we have resolved data, use it
  if (resolvedContractedBy.length > 0) {
    // Single contracted by - show as text
    if (resolvedContractedBy.length === 1) {
      const org = resolvedContractedBy[0]
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            padding: densityStyles.padding,
            cursor: 'pointer',
          }}
          onClick={() => navigate(org?.PK ? `/organizations/${org.PK}/details` : '#')}
        >
          <Typography
            sx={{
              fontSize: densityStyles.fontSize,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {org?.name || 'Unknown'}
          </Typography>
        </Box>
      )
    }

    // Multiple contracted by - show as chips
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: densityStyles.padding,
          gap: 0.5,
          flexWrap: 'wrap',
          overflow: 'hidden',
        }}
      >
        {resolvedContractedBy.slice(0, 2).map(org => (
          <Tooltip key={org.org_uuid} title={org.name} arrow>
            <Chip
              label={org.name}
              size="small"
              onClick={() => navigate(org?.PK ? `/organizations/${org.PK}/details` : '#')}
              sx={{
                fontSize: densityStyles.fontSize,
                height: 'auto',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0.25,
                },
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        ))}
        {resolvedContractedBy.length > 2 && (
          <Tooltip
            title={resolvedContractedBy.slice(2).map(o => o.name).join(', ')}
            arrow
          >
            <Chip
              label={`+${resolvedContractedBy.length - 2}`}
              size="small"
              sx={{
                fontSize: densityStyles.fontSize,
                height: 'auto',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0.25,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    )
  }

  // Fallback to legacy format
  if (legacyContractedBy?.name) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: densityStyles.padding,
          cursor: 'pointer',
        }}
        onClick={() => navigate(legacyContractedBy?.PK ? `/organizations/${legacyContractedBy.PK}/details` : '#')}
      >
        <Typography
          sx={{
            fontSize: densityStyles.fontSize,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {legacyContractedBy.name}
        </Typography>
      </Box>
    )
  }

  // No data
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: densityStyles.padding,
      }}
    >
      <Typography
        sx={{
          fontSize: densityStyles.fontSize,
          color: 'text.disabled',
        }}
      >
        —
      </Typography>
    </Box>
  )
}

export default RenderContractedByCell
